import { Fragment, useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  MapPin, Share2, Printer, ChevronRight,
  Home as HomeIcon, Car, Video, Flame,
  CalendarDays, CheckCircle2, Building2,
  Hash, DollarSign, Maximize, Bed, Bath,
  Layers, School, Receipt, TrendingUp,
  Utensils, ShoppingCart, Trees, Coffee, ShoppingBag, GraduationCap,
  AlignLeft,
} from 'lucide-react';
import { useJsApiLoader, GoogleMap, MarkerF } from '@react-google-maps/api';
import { toast } from 'sonner';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '@/components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PhotoGallery } from '@/components/detail/PhotoGallery';
import { ContactAgentCard } from '@/components/detail/ContactAgentCard';
import { MortgageCalculator } from '@/components/detail/MortgageCalculator';
import { StatusBadge } from '@/components/common/StatusBadge';
import { TransactionTypeBadge } from '@/components/common/TransactionTypeBadge';
import { FavoriteButton } from '@/components/common/FavoriteButton';
import { ListingCard } from '@/components/common/ListingCard';
import { AdSlot } from '@/components/common/AdSlot';
import { SkeletonGrid } from '@/components/common/SkeletonCard';
import { EmptyState } from '@/components/common/EmptyState';
import { cn } from '@/lib/utils';
import { formatPrice, formatNumber } from '@/utils/format';
import { getMlsSource } from '@/utils/mlsSource';
import { MlsBadge } from '@/components/common/MlsBadge';
import { getListingById, getSimilarListings, getAgentById, getMapMarkers } from '@/services/api';
import { BRAND } from '@/config/brand';

// ─── MLS Board ID → readable name map ────────────────────────────────────────

const BOARD_ID_MAP = {
  // Florida
  MIAMI: 'Miami Association of Realtors',
  MIAMIREB: 'Miami Association of Realtors',
  MRSMLS: 'Miami Realtors MLS',
  SEFMLS: 'Southeast Florida MLS',
  BEACHES: 'Beaches MLS',
  BEACHESMLS: 'Beaches MLS',
  RAPB: 'Realtor Association of the Palm Beaches',
  BPBSL: 'Broward, Palm Beaches & St. Lucie Realtors',
  BROWARD: 'Broward County MLS',
  ORBIS: 'Orlando Regional MLS (ORMLS)',
  ORMLS: 'Orlando Regional MLS',
  STELLAR: 'Stellar MLS',
  SWFMLS: 'Southwest Florida MLS',
  NABOR: 'Naples Area Board of Realtors MLS',
  CFBR: 'Central Florida Board of Realtors',
  // Texas
  NTREIS: 'North Texas Real Estate Information Systems',
  HAR: 'Houston Association of Realtors',
  SABOR: 'San Antonio Board of Realtors',
  ACTRIS: 'Austin/Central Texas Realty Information Service',
  // Arizona
  ARMLS: 'Arizona Regional MLS',
  PAAR: 'Prescott Area Association of Realtors',
  // Washington
  NWMLS: 'Northwest MLS',
  // Colorado
  PPMLS: 'Pikes Peak MLS',
  IRES: 'Information Real Estate Services (IRES MLS)',
  REColorado: 'REcolorado',
  RECOLORADO: 'REcolorado',
  // Tennessee
  GNAR: 'Greater Nashville Association of Realtors',
  REALTRACS: 'RealTracs MLS',
  // Georgia
  GAMLS: 'Georgia MLS',
  FMLS: 'First MLS (Georgia)',
  // California
  CRMLS: 'California Regional MLS',
  SFAR: 'San Francisco Association of Realtors',
  BAREIS: 'Bay Area Real Estate Information Services',
  // Canada – Ontario
  TRREB: 'Toronto Regional Real Estate Board',
  TREB: 'Toronto Regional Real Estate Board',
  OREB: 'Ottawa Real Estate Board',
  LSTAR: 'London & St. Thomas Association of Realtors',
  RAHB: 'Realtors Association of Hamilton-Burlington',
  KW: 'Kitchener-Waterloo Association of Realtors',
  // Canada – BC
  REBGV: 'Real Estate Board of Greater Vancouver',
  FVREB: 'Fraser Valley Real Estate Board',
  VIREB: 'Vancouver Island Real Estate Board',
  // Canada – Alberta
  CREA: 'Canadian Real Estate Association',
  AREA: 'Alberta Real Estate Association',
  CREB: 'Calgary Real Estate Board',
  REALTORS_ALBERTA: 'REALTORS® Association of Alberta',
  // Generic
  IDX: 'IDX MLS',
  MLS: 'MLS',
};

function getBoardName(boardId) {
  if (!boardId) return null;
  const upper = String(boardId).toUpperCase().replace(/[-_\s]/g, '');
  if (BOARD_ID_MAP[upper]) return BOARD_ID_MAP[upper];
  // Try original casing key
  if (BOARD_ID_MAP[boardId]) return BOARD_ID_MAP[boardId];
  // Fall back to formatted board ID
  return boardId;
}

// ─── Places API helpers ───────────────────────────────────────────────────────

const PLACES_LIBRARIES = ['marker', 'places'];
const GMAPS_API_KEY = BRAND.googleMapsApiKey;
const MAP_CONTAINER_STYLE = { width: '100%', height: '100%' };

const NEARBY_CATEGORIES = [
  { key: 'restaurant', label: 'Restaurants', icon: Utensils, type: 'restaurant' },
  { key: 'grocery', label: 'Groceries', icon: ShoppingCart, type: 'supermarket' },
  { key: 'park', label: 'Parks', icon: Trees, type: 'park' },
  { key: 'school', label: 'Schools', icon: GraduationCap, type: 'school' },
  { key: 'cafe', label: 'Coffee', icon: Coffee, type: 'cafe' },
  { key: 'shopping', label: 'Shopping', icon: ShoppingBag, type: 'shopping_mall' },
];

const NEARBY_CACHE_PREFIX = 'casahom:nearby:v1:';
const NEARBY_CACHE_MAX = 30;

function readNearbyCache(key) {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeNearbyCache(key, items) {
  try {
    const ss = window.sessionStorage;
    const keys = [];
    for (let i = 0; i < ss.length; i += 1) {
      const k = ss.key(i);
      if (k && k.startsWith(NEARBY_CACHE_PREFIX) && k !== key) keys.push(k);
    }
    while (keys.length >= NEARBY_CACHE_MAX) ss.removeItem(keys.shift());
    ss.setItem(key, JSON.stringify(items));
  } catch {
    /* ignore quota / private mode */
  }
}

// ─── Primitives ───────────────────────────────────────────────────────────────

function SectionCard({
  title,
  icon: Icon,
  subtitle,
  children,
  noPad = false,
  headerExtra = null,
  className,
  ...rest
}) {
  return (
    <section
      className={cn(
        'mt-7 overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm',
        className,
      )}
      {...rest}
    >
      {title && (
        <header className="flex items-center gap-3 border-b border-slate-100 bg-slate-50/60 px-4 py-3 sm:px-5">
          <div className="flex min-w-0 flex-1 items-center gap-2.5">
            <span
              className="h-5 w-0.5 shrink-0 rounded-full bg-primary"
              aria-hidden="true"
            />
            {Icon && (
              <span
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-white text-slate-600 ring-1 ring-inset ring-slate-200/80"
                aria-hidden="true"
              >
                <Icon size={14} strokeWidth={2.25} />
              </span>
            )}
            <div className="min-w-0">
              <h2 className="text-[15px] font-semibold leading-none tracking-tight text-slate-900 sm:text-base">
                {title}
              </h2>
              {subtitle && (
                <p className="mt-1 text-xs leading-snug text-slate-500">{subtitle}</p>
              )}
            </div>
          </div>
          {headerExtra}
        </header>
      )}
      <div className={noPad ? '' : 'px-4 py-4 sm:px-5'}>{children}</div>
    </section>
  );
}

function humanize(token) {
  const s = String(token).trim();
  if (!/[A-Za-z]/.test(s)) return s;
  return s
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([a-z])(\d)/g, '$1 $2')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function hasDetailValue(value) {
  if (value == null || value === '' || value === 0) return false;
  if (Array.isArray(value) && value.length === 0) return false;
  return true;
}

function normalizeDetailValue(value) {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string' && /^(true|false)$/i.test(value.trim())) {
    return /^true$/i.test(value.trim()) ? 'Yes' : 'No';
  }
  return value;
}

function DetailRow({ label, value }) {
  value = normalizeDetailValue(value);
  if (!hasDetailValue(value)) return null;

  const isArray = Array.isArray(value);
  const str = isArray ? null : String(value);
  const strTokens = isArray ? [] : str.split(/\s*,\s*/).map((t) => t.trim()).filter(Boolean);
  const isMultiList =
    isArray ||
    (strTokens.length > 1 && strTokens.every((t) => /^[A-Za-z]/.test(t)));

  if (isMultiList) {
    const rawItems = isArray
      ? value.map((el) => String(el).trim()).filter(Boolean)
      : strTokens;
    const items = [...new Set(rawItems.map(humanize))];
    return (
      <div className="col-span-full py-2.5">
        <dt className="text-sm text-slate-500">{label}</dt>
        <dd className="mt-2 grid grid-cols-1 gap-x-8 gap-y-1.5 sm:grid-cols-2">
          {items.map((item, i) => (
            <span
              key={`${item}-${i}`}
              className="inline-flex min-w-0 items-start gap-2 text-sm font-medium text-slate-900"
            >
              <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-primary" aria-hidden="true" />
              <span className="leading-snug break-words">{item}</span>
            </span>
          ))}
        </dd>
      </div>
    );
  }

  const shouldHumanize =
    typeof value === 'string' &&
    !str.includes('$') &&
    !str.includes('/') &&
    !str.includes('-') &&
    !str.includes(' ') &&
    /[a-z][A-Z]/.test(str);

  const displayValue = shouldHumanize ? humanize(str) : str;

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] items-baseline gap-x-4 py-2.5">
      <dt className="min-w-0 text-sm text-slate-500">{label}</dt>
      <dd className="min-w-0 break-words text-right text-sm font-semibold tabular-nums text-slate-900">
        {displayValue}
      </dd>
    </div>
  );
}

// ─── Key facts inline bar (Movoto-style) ──────────────────────────────────────

function KeyFactsBar({ listing }) {
  const facts = [
    listing.beds ? { icon: Bed, label: listing.beds, sublabel: listing.beds === 1 ? 'Bed' : 'Beds' } : null,
    listing.baths ? { icon: Bath, label: listing.baths, sublabel: listing.baths === 1 ? 'Bath' : 'Baths' } : null,
    listing.sqft ? { icon: Maximize, label: formatNumber(listing.sqft), sublabel: 'Sqft' } : null,
    listing.numGarageSpaces ? { icon: Car, label: listing.numGarageSpaces, sublabel: 'Garage' } : null,
  ].filter(Boolean);

  if (!facts.length) return null;

  return (
    <div className="mt-4 flex flex-wrap items-center gap-6">
      {facts.map((f, i) => (
        <div key={i} className="flex items-center gap-2">
          <f.icon size={20} className="text-slate-400" />
          <div>
            <span className="text-xl font-semibold text-slate-900">{f.label}</span>
            <span className="ml-1 text-sm text-slate-500">{f.sublabel}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Home highlights (compact inline) ────────────────────────────────────────

function CompactHighlights({ listing }) {
  const items = [
    { label: 'Year built', value: listing.yearBuilt ?? null },
    { label: 'Property type', value: listing.type || null },
    { label: 'Lot size', value: listing.lotSize ? `${formatNumber(listing.lotSize)} sqft` : null },
    { label: 'Price/sqft', value: listing.pricePerSqft ? `$${formatNumber(listing.pricePerSqft)}` : null },
    { label: 'HOA', value: listing.hoa ? `$${formatNumber(listing.hoa)}/mo` : null },
    { label: 'Days on market', value: listing.daysOnMarket != null ? listing.daysOnMarket : null },
  ].filter((i) => i.value != null && i.value !== '');

  if (!items.length) return null;

  return (
    <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3 text-sm">
      {items.map(({ label, value }) => (
        <div key={label}>
          <dt className="text-slate-500 text-xs">{label}</dt>
          <dd className="font-semibold text-slate-900 mt-0.5">{value}</dd>
        </div>
      ))}
    </div>
  );
}

// ─── Description ─────────────────────────────────────────────────────────────

function DescriptionSection({ text }) {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;
  const isLong = text.length > 420;
  return (
    <div className="max-w-[42rem]">
      <p
        className={cn(
          'text-[15px] leading-[1.65] text-slate-600 whitespace-pre-line',
          !expanded && isLong && 'line-clamp-5',
        )}
      >
        {text}
      </p>
      {isLong && (
        <div className="mt-3.5 flex items-center gap-3">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="inline-flex items-center gap-1 rounded-md text-sm font-semibold text-primary transition-colors hover:text-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            aria-expanded={expanded}
          >
            {expanded ? 'Show less' : 'Read full description'}
            <ChevronRight
              size={14}
              className={cn('transition-transform duration-200', expanded && 'rotate-90')}
              aria-hidden="true"
            />
          </button>
          <span className="h-px flex-1 bg-slate-100" aria-hidden="true" />
        </div>
      )}
    </div>
  );
}

// ─── MLS Attribution ─────────────────────────────────────────────────────────

function MLSAttribution({ listing }) {
  const parts = [listing.agentName, listing.officeName].filter(Boolean);
  const hasParts = parts.length > 0;
  const mlsSrc = getMlsSource(listing);
  const boardName = (mlsSrc && mlsSrc.key !== 'mls')
    ? mlsSrc.name
    : (getBoardName(listing.source || listing.boardId) || mlsSrc?.name || null);
  const hasMeta = listing.mlsNumber || boardName || listing.updatedOn;
  if (!hasParts && !hasMeta) return null;

  const formatDate = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return iso;
    }
  };

  return (
    <div className="mt-4 border-t border-slate-100 pt-3 text-xs leading-relaxed text-slate-400 space-y-0.5">
      {boardName && (
        <p className="font-medium text-slate-500">
          Listing provided by <span className="text-slate-700">{boardName}</span>
        </p>
      )}
      {hasParts && <p>Listed by: {parts.join(' · ')}</p>}
      <p>
        {listing.mlsNumber && `MLS# ${listing.mlsNumber}`}
        {listing.updatedOn && ` · Updated ${formatDate(listing.updatedOn)}`}
      </p>
    </div>
  );
}

// ─── Property details (categorized accordion / tabs) ──────────────────────────

function CategoryRows({ rows }) {
  const filtered = rows.filter(([, v]) => hasDetailValue(normalizeDetailValue(v)));
  if (!filtered.length) return null;
  return (
    <dl className="grid grid-cols-1 md:grid-cols-2 md:gap-x-10 [&>*]:border-b [&>*]:border-slate-100">
      {filtered.map(([k, v]) => (
        <DetailRow key={k} label={k} value={v} />
      ))}
    </dl>
  );
}

function PropertyDetailsSection({ listing }) {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches,
  );

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const str = (v) => {
    if (v == null || v === '') return null;
    if (Array.isArray(v)) return v.length === 0 ? null : v;
    return v;
  };

  const amenityItems = [
    ...(Array.isArray(listing.amenities) ? listing.amenities : []),
    ...(listing.extras
      ? String(listing.extras).split(/[,;/]+/).map((s) => s.trim()).filter(Boolean)
      : []),
  ];
  const uniqueAmenities = [...new Set(amenityItems.filter(Boolean))];

  const categories = [
    {
      title: 'Overview',
      rows: [
        ['Property type', str(listing.type)],
        ['Style', str(listing.style)],
        ['Year built', listing.yearBuilt || null],
        ['Stories', listing.stories || null],
        ['Sqft', listing.sqft ? `${formatNumber(listing.sqft)} sqft` : null],
        ['Property condition', str(listing.propertyCondition)],
        ['New construction', str(listing.newConstructionYN)],
        ['Days on market', listing.daysOnMarket != null ? `${listing.daysOnMarket} days` : null],
        ['MLS #', str(listing.mlsNumber)],
      ],
    },
    {
      title: 'Interior',
      rows: [
        ['Total bedrooms', listing.beds || null],
        ['Full bathrooms', listing.baths || null],
        ['Half bathrooms', listing.numBathroomsPlus || null],
        ['Total rooms', listing.numRooms || null],
        ['Flooring', str(listing.flooring)],
        ['Basement', str(listing.basement)],
        ['Fireplaces', listing.numFireplaces || null],
        ['Appliances', str(listing.appliances)],
        ['Interior features', str(listing.interiorFeatures)],
        ['Laundry', str(listing.laundryFeatures)],
      ],
    },
    {
      title: 'Heating & Cooling',
      rows: [
        ['Heating', str(listing.heating)],
        ['Cooling / AC', str(listing.cooling)],
        ['Utilities', str(listing.utilities)],
      ],
    },
    {
      title: 'Parking',
      rows: [
        ['Parking spaces', listing.parking || null],
        ['Garage spaces', listing.numGarageSpaces || null],
        ['Garage', str(listing.garage)],
      ],
    },
    {
      title: 'Exterior & Lot',
      rows: [
        ['Lot size', listing.lotSize ? `${formatNumber(listing.lotSize)} sqft` : null],
        ['Lot (acres)', listing.lotAcres ? `${listing.lotAcres} ac` : null],
        ['Foundation', str(listing.foundation)],
        ['Exterior', str(listing.exterior)],
        ['Roofing', str(listing.roofing)],
        ['Pool / Spa', str(listing.pool)],
        ['Waterfront', str(listing.waterfront)],
        ['View', str(listing.view)],
        ['Fencing', str(listing.fencing)],
      ],
    },
    {
      title: 'Community & HOA',
      rows: [
        ['HOA fee', listing.hoa ? `$${formatNumber(listing.hoa)}/mo` : null],
        ['HOA includes', str(listing.associationFeeIncludes)],
        ['Subdivision', str(listing.subdivision)],
        ['Neighborhood', str(listing.neighborhood)],
        ['Pets allowed', str(listing.petsAllowed)],
        ['Zoning', str(listing.zoning)],
      ],
    },
    {
      title: 'Financial',
      rows: [
        ['List price', listing.price ? formatPrice(listing.price) : null],
        ['Original price', listing.originalPrice ? formatPrice(listing.originalPrice) : null],
        ['Price / sqft', listing.pricePerSqft ? `$${formatNumber(listing.pricePerSqft)}` : null],
        ['Annual taxes', listing.taxesAnnual ? `$${formatNumber(listing.taxesAnnual)}` : listing.taxes ? `$${formatNumber(listing.taxes)}` : null],
        ['Tax year', listing.assessmentYear || str(listing.taxesDate)],
      ],
    },
    {
      title: 'Amenities',
      rows: [
        ['Amenities', uniqueAmenities.length ? uniqueAmenities : null],
      ],
    },
  ];

  const visibleCategories = categories
    .map((cat) => ({
      ...cat,
      id: cat.title.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      rows: cat.rows.filter(([, v]) => hasDetailValue(normalizeDetailValue(v))),
    }))
    .filter((cat) => cat.rows.length > 0);

  if (!visibleCategories.length) return null;

  const defaultId = visibleCategories[0].id;

  return (
    <SectionCard title="Property details" icon={Building2} noPad>
      {isDesktop ? (
        <Tabs defaultValue={defaultId}>
          <div className="relative border-b border-slate-100 bg-white">
            <div
              className="pointer-events-none absolute inset-y-0 right-0 z-[1] w-8 bg-gradient-to-l from-white to-transparent"
              aria-hidden="true"
            />
            <TabsList
              className="no-scrollbar h-auto w-full justify-start gap-0 overflow-x-auto rounded-none bg-transparent px-2 py-0 sm:px-3"
              aria-label="Property detail categories"
            >
              {visibleCategories.map((cat) => (
                <TabsTrigger
                  key={cat.id}
                  value={cat.id}
                  className={cn(
                    'relative shrink-0 rounded-none border-b-2 border-transparent px-3.5 py-2.5 text-sm font-medium text-slate-500 shadow-none',
                    'hover:text-slate-800',
                    'focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                    'data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-slate-900 data-[state=active]:shadow-none',
                  )}
                >
                  {cat.title}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          {visibleCategories.map((cat) => (
            <TabsContent
              key={cat.id}
              value={cat.id}
              className="mt-0 px-4 py-1 sm:px-5 focus-visible:ring-0"
            >
              <CategoryRows rows={cat.rows} />
            </TabsContent>
          ))}
        </Tabs>
      ) : (
        <Accordion type="single" collapsible defaultValue={defaultId}>
          {visibleCategories.map((cat) => (
            <AccordionItem
              key={cat.id}
              value={cat.id}
              className="border-slate-100 px-4 last:border-b-0"
            >
              <AccordionTrigger className="py-3.5 text-sm font-semibold text-slate-900 hover:no-underline hover:text-primary focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 data-[state=open]:text-primary">
                <span className="flex items-center gap-2.5">
                  {cat.title}
                  <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-slate-500 ring-1 ring-inset ring-slate-200/80">
                    {cat.rows.length}
                  </span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="pb-3 pt-0">
                <CategoryRows rows={cat.rows} />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </SectionCard>
  );
}

// ─── Open houses ─────────────────────────────────────────────────────────────

function OpenHousesSection({ openHouses }) {
  if (!openHouses?.length) return null;
  return (
    <SectionCard title="Open houses" icon={CalendarDays} noPad>
      <ul className="divide-y divide-slate-100">
        {openHouses.map((oh, i) => (
          <li key={i} className="flex items-start gap-3.5 px-4 py-3.5 sm:px-5">
            <CalendarDays size={18} className="mt-0.5 shrink-0 text-primary" aria-hidden="true" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900">
                {oh.date ?? oh.startTime ?? '—'}
              </p>
              {(oh.startTime || oh.endTime) && (
                <p className="mt-0.5 text-sm text-slate-500">
                  {oh.startTime}{oh.endTime ? ` – ${oh.endTime}` : ''}
                </p>
              )}
              {oh.remarks && (
                <p className="mt-1 text-sm text-slate-500">{oh.remarks}</p>
              )}
            </div>
          </li>
        ))}
      </ul>
    </SectionCard>
  );
}

// ─── Property & price history ─────────────────────────────────────────────────

function PropertyHistorySection({ priceHistory, taxes, taxesDate }) {
  const hasHistory = Array.isArray(priceHistory) && priceHistory.length > 0;
  const hasTax = taxes != null;
  if (!hasHistory && !hasTax) return null;

  return (
    <SectionCard title="Property & tax history" icon={TrendingUp} noPad={hasHistory}>
      {hasTax && (
        <div className={cn('grid grid-cols-1 sm:grid-cols-2 gap-x-6', hasHistory ? 'px-4 pt-3 sm:px-5' : '', hasHistory && 'mb-1')}>
          <DetailRow label="Annual taxes" value={`$${formatNumber(taxes)}`} />
          {taxesDate && <DetailRow label="Tax year" value={taxesDate} />}
        </div>
      )}
      {hasHistory && (
        <div className={cn(hasTax && 'border-t border-slate-100')}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Event</TableHead>
                <TableHead className="text-right">Price</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {priceHistory.map((h, i) => (
                <TableRow key={i}>
                  <TableCell className="text-slate-500">{h.date ?? '—'}</TableCell>
                  <TableCell>{h.event ?? h.status ?? '—'}</TableCell>
                  <TableCell className="text-right font-medium tabular-nums">
                    {h.price ? formatPrice(h.price) : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </SectionCard>
  );
}

// ─── Map ─────────────────────────────────────────────────────────────────────

function MapSection({ listing }) {
  const centerLat = Number(listing?.lat);
  const centerLng = Number(listing?.lng);
  const hasCoords = Number.isFinite(centerLat) && Number.isFinite(centerLng);
  const [mapType, setMapType] = useState('roadmap');
  const [nearbyHomes, setNearbyHomes] = useState([]);
  const mapRef = useRef(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GMAPS_API_KEY,
    libraries: PLACES_LIBRARIES,
    id: 'casahom-gmaps',
  });

  useEffect(() => {
    if (!listing?.zip || !hasCoords) return;
    getMapMarkers({ filters: { zip: listing.zip }, limit: 60 })
      .then((data) => {
        setNearbyHomes(
          (data.listings || []).filter((h) => h.id !== listing.id && h.lat && h.lng),
        );
      })
      .catch(() => setNearbyHomes([]));
  }, [listing, hasCoords]);

  useEffect(() => {
    if (mapRef.current) mapRef.current.setMapTypeId(mapType);
  }, [mapType]);

  if (!hasCoords) return null;
  if (loadError || !GMAPS_API_KEY) return null;

  return (
    <SectionCard title="Location & Neighborhood" icon={MapPin} noPad>
      <div className="relative h-96 overflow-hidden">
        <div
          className="absolute right-3 top-3 z-10 flex overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm"
          role="group"
          aria-label="Map type"
        >
          <button
            type="button"
            onClick={() => setMapType('roadmap')}
            className={cn(
              'px-3 py-1.5 text-sm font-medium transition-colors',
              mapType === 'roadmap'
                ? 'bg-slate-900 text-white'
                : 'text-slate-700 hover:bg-slate-50',
            )}
            aria-pressed={mapType === 'roadmap'}
          >
            Map
          </button>
          <button
            type="button"
            onClick={() => setMapType('hybrid')}
            className={cn(
              'px-3 py-1.5 text-sm font-medium transition-colors',
              mapType === 'hybrid'
                ? 'bg-slate-900 text-white'
                : 'text-slate-700 hover:bg-slate-50',
            )}
            aria-pressed={mapType === 'hybrid'}
          >
            Satellite
          </button>
        </div>
        {!isLoaded ? (
          <div className="h-full w-full animate-pulse bg-slate-200" aria-hidden="true" />
        ) : (
          <GoogleMap
            center={{ lat: centerLat, lng: centerLng }}
            zoom={15}
            mapContainerStyle={MAP_CONTAINER_STYLE}
            onLoad={(map) => {
              mapRef.current = map;
              map.setMapTypeId(mapType);
            }}
            onUnmount={() => { mapRef.current = null; }}
            options={{
              mapTypeId: mapType,
              mapTypeControl: false,
              streetViewControl: false,
              fullscreenControl: true,
              clickableIcons: false,
            }}
          >
            <MarkerF
              position={{ lat: centerLat, lng: centerLng }}
              icon={{
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 10,
                fillColor: '#007AFF',
                fillOpacity: 1,
                strokeColor: '#fff',
                strokeWeight: 2,
              }}
            />
            {nearbyHomes.map((home) => (
              <MarkerF
                key={home.id}
                position={{ lat: home.lat, lng: home.lng }}
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 6,
                  fillColor: '#64748b',
                  fillOpacity: 0.8,
                  strokeColor: '#fff',
                  strokeWeight: 1,
                }}
              />
            ))}
          </GoogleMap>
        )}
      </div>
    </SectionCard>
  );
}

// ─── Schools ─────────────────────────────────────────────────────────────────

function SchoolsSection({ schools, elementary, middle, high }) {
  const schoolItems = [];
  if (Array.isArray(schools) && schools.length > 0) {
    schoolItems.push(...schools);
  } else {
    if (elementary) schoolItems.push({ name: elementary, type: 'Elementary' });
    if (middle) schoolItems.push({ name: middle, type: 'Middle' });
    if (high) schoolItems.push({ name: high, type: 'High' });
  }
  if (!schoolItems.length) return null;
  return (
    <SectionCard title="Nearby schools" icon={School} noPad data-testid="nearby-schools">
      <ul className="divide-y divide-slate-100">
        {schoolItems.map((s, i) => (
          <li
            key={s.name || i}
            className="flex items-center justify-between gap-3 px-4 py-3.5 sm:px-5"
          >
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900">{s.name}</p>
              <p className="mt-0.5 text-sm text-slate-500">
                {[s.type, s.grades, s.distance != null ? `${s.distance} mi` : null]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </div>
            {s.rating != null && (
              <span className="ml-2 shrink-0 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold tabular-nums text-amber-800">
                {s.rating}/10
              </span>
            )}
          </li>
        ))}
      </ul>
    </SectionCard>
  );
}

// ─── MLS Disclaimer ──────────────────────────────────────────────────────────

function MLSDisclaimer({ listing }) {
  const mlsSrc = getMlsSource(listing);
  const boardName = (mlsSrc && mlsSrc.key !== 'mls')
    ? mlsSrc.name
    : (getBoardName(listing.source || listing.boardId) || mlsSrc?.name || 'MLS');
  return (
    <p className="mt-10 text-center text-xs text-slate-400 px-4">
      Listing information courtesy of {boardName}
      {listing.mlsNumber ? ` · MLS# ${listing.mlsNumber}` : ''}.
      Information is deemed reliable but not guaranteed. Data may not be used for any purpose other than to identify prospective properties.
    </p>
  );
}

// ─── What's Nearby ────────────────────────────────────────────────────────────

const NEARBY_PREVIEW = 5;

const SECTION_CATEGORIES = [
  NEARBY_CATEGORIES.find(({ key }) => key === 'grocery'),
  NEARBY_CATEGORIES.find(({ key }) => key === 'restaurant'),
  NEARBY_CATEGORIES.find(({ key }) => key === 'park'),
  NEARBY_CATEGORIES.find(({ key }) => key === 'cafe'),
].filter(Boolean);

function WhatsNearby({ lat, lng, address }) {
  const [results, setResults] = useState(null);
  const [activeKey, setActiveKey] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!lat || !lng) return;

    const controller = new AbortController();
    const cacheKey = `${NEARBY_CACHE_PREFIX}${lat.toFixed(4)},${lng.toFixed(4)}`;

    const apply = (items) => {
      const pending = {};
      SECTION_CATEGORIES.forEach(({ key }) => { pending[key] = []; });
      (items || []).forEach((r) => {
        const cat = SECTION_CATEGORIES.find(({ type }) => type === r.primary_type);
        if (!cat) return;
        pending[cat.key].push({ name: r.name, distance: (r.distance_m || 0) / 1609.344 });
      });
      SECTION_CATEGORIES.forEach(({ key }) => pending[key].sort((a, b) => a.distance - b.distance));
      setResults(pending);
      const first = SECTION_CATEGORIES.find(({ key: k }) => pending[k]?.length > 0);
      if (first) setActiveKey(first.key);
    };

    const cached = readNearbyCache(cacheKey);
    if (cached) { apply(cached); return () => controller.abort(); }

    (async () => {
      try {
        const params = new URLSearchParams({
          lat: String(lat),
          lng: String(lng),
          type: SECTION_CATEGORIES.map((c) => c.type).join(','),
          radius_m: '8000',
          limit: '60',
        });
        const res = await fetch(`/api/v1/places/nearby/?${params}`, { signal: controller.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const items = Array.isArray(data?.results) ? data.results : [];
        if (controller.signal.aborted) return;
        writeNearbyCache(cacheKey, items);
        apply(items);
      } catch {
        if (!controller.signal.aborted) apply([]);
      }
    })();

    return () => controller.abort();
  }, [lat, lng]);

  if (!lat || !lng) return null;
  if (!results) return null;

  const hasAny = SECTION_CATEGORIES.some(({ key }) => results[key]?.length > 0);
  if (!hasAny) return null;

  const activeCat = SECTION_CATEGORIES.find(({ key }) => key === activeKey);
  const activePlaces = (activeKey && results[activeKey]) || [];
  const visiblePlaces = expanded ? activePlaces : activePlaces.slice(0, NEARBY_PREVIEW);
  const hasMore = activePlaces.length > NEARBY_PREVIEW;

  return (
    <section className="mt-8">
      <div className="mb-4 flex items-center gap-2">
        <Coffee size={20} className="text-blue-600 shrink-0" />
        <h2 className="text-xl font-semibold text-slate-900">Nearby places of interest</h2>
      </div>
      {address && <p className="text-sm text-slate-500 mb-4">Places around {address}</p>}

      {/* Category chips */}
      <div className="flex flex-wrap gap-2 mb-4">
        {SECTION_CATEGORIES.map(({ key, label }) => {
          if (!results[key]?.length) return null;
          const isActive = key === activeKey;
          return (
            <button
              key={key}
              onClick={() => { setActiveKey(key); setExpanded(false); }}
              className={cn(
                'rounded-lg border px-4 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-blue-50 text-blue-700 border-blue-200 font-semibold'
                  : 'border-slate-200 text-slate-700 hover:border-blue-300 bg-white',
              )}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* Places list */}
      {activeCat && (
        <div className="space-y-3 mb-4">
          {visiblePlaces.map((p, i) => (
            <div key={i} className="rounded-xl border border-slate-200 bg-white px-5 py-4 flex items-center gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-blue-50">
                <activeCat.icon size={20} className="text-blue-600" />
              </span>
              <span className="flex-1 text-base font-medium text-slate-900">{p.name}</span>
              <span className="shrink-0 tabular-nums text-sm text-slate-500">{p.distance.toFixed(2)} mi</span>
            </div>
          ))}
        </div>
      )}

      {/* View more / less */}
      {hasMore && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700"
        >
          {expanded ? 'Show less' : 'View more'}
          <ChevronRight size={16} className={cn('transition-transform', expanded && 'rotate-90')} />
        </button>
      )}
    </section>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function ListingDetail() {
  const { id } = useParams();
  const [listing, setListing] = useState(undefined);
  const [error, setError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [similar, setSimilar] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setListing(undefined);
    setError(false);
    setSimilar([]);
    let alive = true;
    getListingById(id)
      .then((l) => {
        if (!alive) return;
        setListing(l);
        if (l) getSimilarListings(l, 4).then((s) => { if (alive) setSimilar(s); });
      })
      .catch(() => {
        if (!alive) return;
        setListing(null);
        setError(true);
      });
    return () => { alive = false; };
  }, [id, retryKey]);

  if (listing === undefined) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="h-[420px] animate-pulse rounded-2xl bg-slate-200" />
        <div className="mt-8">
          <SkeletonGrid count={3} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20">
        <EmptyState
          title="Could not load listing"
          description="Something went wrong while loading this home. Please try again."
          actionLabel="Try again"
          onAction={() => setRetryKey((k) => k + 1)}
        />
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20">
        <EmptyState
          title="Listing not found"
          description="This home may have been sold or removed."
          actionLabel="Back to listings"
          onAction={() => (window.location.href = '/listings')}
        />
      </div>
    );
  }

  const agent = getAgentById(listing.agentId);

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        toast('Link copied to clipboard');
        return;
      }
      throw new Error('clipboard unavailable');
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        if (ok) {
          toast('Link copied to clipboard');
          return;
        }
      } catch (_) { /* fall through */ }
      toast.error('Could not copy link. Copy from the address bar instead.');
    }
  };

  const cityLine = [listing.city, listing.state].filter(Boolean).join(', ');
  const fullCityLine = [cityLine, listing.zip].filter(Boolean).join(' ');

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Breadcrumb + Actions */}
        <div className="flex items-center justify-between mb-5">
          <nav
            className="flex items-center gap-1 text-sm text-slate-500"
            data-testid="breadcrumbs"
            aria-label="Breadcrumb"
          >
            <Link to="/" className="hover:text-blue-600" aria-label="Home">
              <HomeIcon size={14} />
            </Link>
            <ChevronRight size={14} />
            <Link to="/map" className="hover:text-blue-600">
              Map
            </Link>
            <ChevronRight size={14} />
            {listing.lat && listing.lng ? (
              <Link
                to={`/map?lat=${listing.lat}&lng=${listing.lng}&zoom=17`}
                className="truncate hover:text-blue-600 text-slate-700"
              >
                {listing.streetAddress || listing.address}
              </Link>
            ) : (
              <span className="truncate text-slate-700">
                {listing.streetAddress || listing.address}
              </span>
            )}
          </nav>
          <div className="hidden sm:flex items-center gap-2">
            <FavoriteButton id={listing.id} />
            <button
              onClick={share}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
              aria-label="Share"
              data-testid="share-button"
            >
              <Share2 size={16} />
            </button>
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
              aria-label="Print"
              data-testid="print-button"
            >
              <Printer size={16} />
            </button>
          </div>
        </div>

        {/* Photo gallery */}
        <PhotoGallery
          photos={listing.photos}
          alt={`${listing.streetAddress || listing.address}, ${listing.city}`}
          virtualTourUrl={listing.virtualTourUrl}
        />

        {/* Two-column layout */}
        <div className="mt-8 lg:grid lg:grid-cols-[1fr_380px] lg:gap-10">
          {/* ── Left main column ── */}
          <div>
            {/* Header: price, address, key facts */}
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
              {/* Badges row */}
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge listing={listing} />
                <TransactionTypeBadge isRental={listing.isRental} />
                {listing.isNewConstruction && (
                  <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
                    New Construction
                  </span>
                )}
              </div>

              {/* Price */}
              <p
                className="mt-5 font-display text-5xl font-bold tabular-nums text-slate-900"
                data-testid="detail-price"
              >
                {formatPrice(listing.price, listing.isRental)}
              </p>

              {/* Address */}
              <div className="mt-4">
                <p className="text-xl font-semibold text-slate-900">
                  {listing.streetAddress || listing.address}
                </p>
                {fullCityLine && (
                  <p className="flex items-center gap-1.5 text-slate-500 text-base mt-1">
                    <MapPin size={16} className="shrink-0 text-slate-400" />
                    {fullCityLine}
                  </p>
                )}
              </div>

              {/* Key facts bar */}
              <KeyFactsBar listing={listing} />

              {/* Compact highlights */}
              <div className="mt-6 pt-6 border-t border-slate-100">
                <CompactHighlights listing={listing} />
              </div>

              {/* Mobile actions */}
              <div className="mt-6 flex sm:hidden items-center gap-2">
                <FavoriteButton id={listing.id} size="lg" />
                <button
                  onClick={share}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
                  aria-label="Share"
                  data-testid="share-button-mobile"
                >
                  <Share2 size={15} /> Share
                </button>
                <button
                  onClick={() => window.print()}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
                  aria-label="Print"
                  data-testid="print-button-mobile"
                >
                  <Printer size={15} /> Print
                </button>
              </div>
            </div>

            {/* About / Description */}
            <SectionCard title="About this home" icon={AlignLeft}>
              <DescriptionSection text={listing.description} />
              <MLSAttribution listing={listing} />
            </SectionCard>

            {/* Open houses */}
            <OpenHousesSection openHouses={listing.openHouses} />

            {/* Property details (categorized, includes Amenities) */}
            <PropertyDetailsSection listing={listing} />

            {/* Property & tax history */}
            <PropertyHistorySection
              priceHistory={listing.priceHistory}
              taxes={listing.taxes}
              taxesDate={listing.taxesDate}
            />

            {/* Map */}
            <MapSection listing={listing} />

            {/* Schools */}
            <SchoolsSection
              schools={listing.schools}
              elementary={listing.elementarySchool}
              middle={listing.middleOrJuniorSchool}
              high={listing.highSchool}
            />

            {/* What's nearby */}
            <WhatsNearby lat={listing.lat} lng={listing.lng} address={listing.streetAddress || listing.address} />
          </div>

          {/* ── Right sidebar ── */}
          <aside className="mt-8 lg:mt-0 space-y-6">
            <div className="sticky top-6 space-y-6">
              <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                <div className="flex items-center gap-2 border-b border-slate-100 px-6 py-4">
                  <DollarSign size={20} className="text-blue-600 shrink-0" />
                  <h2 className="text-lg font-semibold text-slate-900">Monthly payment</h2>
                </div>
                <div className="px-6 py-5">
                  <MortgageCalculator price={listing.price} hoa={listing.hoa} variant="compact" />
                </div>
              </div>
              <AdSlot slotId="listings-sidebar-c" size="box" />
              <ContactAgentCard listing={listing} />
              <AdSlot slotId="detail-sidebar-d" size="box" />
              <AdSlot slotId="detail-content-b" size="box" />
            </div>
          </aside>
        </div>

        {/* Similar / nearby homes */}
        {similar.length > 0 && (
          <section className="mt-16">
            <h2 className="font-display text-2xl font-semibold text-slate-900">
              Similar homes nearby
            </h2>
            <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {similar.map((l) => (
                <ListingCard key={l.id} listing={l} />
              ))}
            </div>
          </section>
        )}

        {/* MLS Disclaimer */}
        <MLSDisclaimer listing={listing} />
      </div>
    </div>
  );
}
